/**
 * SLS WebTracking 日志上报封装
 *
 * 当前使用匿名模式，用于临时验证阶段。
 * 验证完成后应切换到 STS 安全模式。
 *
 * 配置说明：
 * - host: SLS endpoint，如 'cn-shenzhen.log.aliyuncs.com'
 * - project: SLS Project 名称
 * - logstore: SLS Logstore 名称（建议使用独立的临时验证 Logstore）
 */
import WebTrackerBrowser from '@aliyun-sls/web-track-browser';

let tracker = null;
let isTrackerReady = false;

const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

function devLog(...args) {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
}

function devWarn(...args) {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(...args);
    }
}

function devError(...args) {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.error(...args);
    }
}

/**
 * 初始化 SLS Tracker
 * @param {Object} options
 * @param {string} options.host
 * @param {string} options.project
 * @param {string} options.logstore
 * @param {Function} [options.getSTSToken] - 生产环境 STS 模式使用
 */
export function initSlsTracker(options = {}) {
    if (tracker) return tracker;

    const { host, project, logstore, getSTSToken } = options;

    if (!host || !project || !logstore) {
        devWarn('[SLS] host/project/logstore 未配置，SLS 上报未初始化');
        return null;
    }

    try {
        tracker = new WebTrackerBrowser({
            host,
            project,
            logstore
        });

        // 生产环境使用 STS 模式
        if (typeof getSTSToken === 'function') {
            // TODO: 接入 @aliyun-sls/web-track-browser STS 插件
            devLog('[SLS] STS mode enabled');
        }

        isTrackerReady = true;
        devLog('[SLS] tracker initialized', { host, project, logstore, mode: getSTSToken ? 'STS' : 'anonymous' });
    } catch (err) {
        devError('[SLS] tracker init failed', err);
        isTrackerReady = false;
    }

    return tracker;
}

/**
 * 发送日志到 SLS
 * @param {Object} data
 */
export function sendSlsLog(data = {}) {
    if (!isTrackerReady || !tracker) {
        // 未初始化时静默跳过，避免报错影响业务
        return;
    }

    try {
        tracker.send(data);
    } catch (err) {
        devError('[SLS] send failed', err, data);
    }
}

/**
 * 获取 tracker 初始化状态
 */
export function isSlsReady() {
    return isTrackerReady;
}
